import React, {useEffect, useState} from 'react';
import { useNavigate } from 'react-router-dom';
import { Client, Pago, Suscripcion } from '../../types/index';
import ConfirmationModal from '../common/ConfirmationModal';
import { Calendar, ChevronDown, ChevronUp, Euro, Trash2, Eye, Users, X, Printer } from 'lucide-react';
import { useClientContext } from '../../contexts/ClientContext';
import { toast } from 'react-toastify';
import parse from 'html-react-parser';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { PlazasCliente } from '../plazas/PlazasCliente';
import OtherPaymentsPrintList from './OtherPaymentsPrintList';
import { useRole } from '../../hooks/useRole';
import { useDeleteClientSubscription, useClientsAndSubscriptionsQuery } from '../../services/hooks/useClienteSuscripciones';
import { useCreatePagoMutation, useUpdatePagoMutation, useDeletePagoMutation } from '../../services/hooks/usePagos';
import Button from '../common/Button';
import { IconButton, IconButtonSlot } from '../common/IconButton';
import OtherPaymentsFilter, { OtherPaymentsFilterState, MetodoPagoFiltro } from './OtherPaymentsFilter';
import { useTenantCode } from '../../services/hooks/useTenantCode';
import { formatPendingMonths } from '../../utils/utils';


interface Month {
    name: string;
    index: number;
    year: number;
    displayName?: string;
    pago?: Pago;
}


// Función mejorada: muestra últimos 3 meses + mes actual + próximos 11 meses (15 meses total)
const getMonthsRange = (pagos: Pago[], monthsBack: number = 3, monthsForward: number = 11) => {
    const months = [
        { name: "Ene", index: 1 },
        { name: "Feb", index: 2 },
        { name: "Mar", index: 3 },
        { name: "Abr", index: 4 },
        { name: "May", index: 5 },
        { name: "Jun", index: 6 },
        { name: "Jul", index: 7 },
        { name: "Ago", index: 8 },
        { name: "Sep", index: 9 },
        { name: "Oct", index: 10 },
        { name: "Nov", index: 11 },
        { name: "Dic", index: 12 }
    ];
    
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1; // 1-12
    const currentYear = currentDate.getFullYear();
    const monthsRange = [];

    // Calcular el rango total: desde -monthsBack hasta +monthsForward
    const totalMonths = monthsBack + monthsForward + 1; // +1 para incluir mes actual
    
    for (let i = -monthsBack; i <= monthsForward; i++) {
        // Calcular el mes y año correctos
        let targetMonth = currentMonth + i;
        let targetYear = currentYear;
        
        // Ajustar si nos salimos del rango 1-12
        while (targetMonth < 1) {
            targetMonth += 12;
            targetYear -= 1;
        }
        while (targetMonth > 12) {
            targetMonth -= 12;
            targetYear += 1;
        }
        
        const monthData = months.find(m => m.index === targetMonth);
        if (!monthData) continue;
        
        const pago = pagos.find(p => p.mes === targetMonth && p.anio === targetYear);
        
        monthsRange.push({
            ...monthData,
            year: targetYear,
            displayName: `${monthData.name} ${targetYear.toString().slice(-2)}`, // "Ene 26"
            pago
        });
    }
    
    return monthsRange;
};


/**
 * La tira de meses de una suscripción, ahora dentro de la fila.
 *
 * Estaba dentro de la expansión, así que marcar un cobro obligaba a abrir el
 * cliente primero. SEPA la enseña en la fila y deja en el detalle solo los datos
 * de referencia; esto hace lo mismo.
 *
 * Son 15 meses (3 atrás + el actual + 11 adelante), no los 12 de SEPA, así que
 * no caben en una sola línea: van en `grid-cols-5`, tres filas. La celda se
 * queda con el mes y el importe; la fecha de cobro pasa al tooltip, que es
 * donde ya estaba el estado.
 */
const MesesSuscripcion: React.FC<{
    suscripcion: Suscripcion;
    onMonthClick: (month: Month, suscripcionId: string) => void;
    onDeletePayment: (paymentId: string) => void;
}> = ({ suscripcion, onMonthClick, onDeletePayment }) => {
    const hoy = new Date();
    const mesActual = hoy.getMonth() + 1;
    const anioActual = hoy.getFullYear();

    return (
        <div className="grid grid-cols-5 gap-1">
            {getMonthsRange(suscripcion.pagos).map((month) => {
                const esMesActual = month.index === mesActual && month.year === anioActual;
                const { pago } = month;
                const estado = !pago ? 'Sin registrar' : pago.estado;
                const fecha = pago?.fechaPago ? new Date(pago.fechaPago).toLocaleDateString() : null;

                return (
                    <div key={`${month.year}-${month.index}`} className="relative">
                        <button
                            onClick={() => onMonthClick(month, suscripcion._id)}
                            className={`w-full px-1 py-1 text-xs rounded transition-colors text-white ${
                                !pago
                                    ? 'bg-gray-500 hover:bg-gray-600'
                                    : pago.estado === 'Cerrado'
                                        ? 'bg-green-500 hover:bg-green-600'
                                        : 'bg-red-500 hover:bg-red-600'
                            } ${esMesActual ? 'ring-2 ring-yellow-400' : ''}`}
                            title={`${month.displayName} — ${estado}${fecha ? ` · ${fecha}` : ''}`}
                        >
                            <span className="block font-bold leading-tight">{month.displayName}</span>
                            {pago && (
                                <span className="block font-semibold leading-tight">{pago.importe}€</span>
                            )}
                        </button>
                        {pago && (
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (pago._id) onDeletePayment(pago._id);
                                }}
                                className="absolute -top-1 -right-1 p-0.5 text-white bg-red-700 rounded-full z-10 hover:bg-red-800"
                                title="Eliminar pago"
                                aria-label={`Eliminar el pago de ${month.displayName}`}
                            >
                                <X size={10} className="font-extrabold" />
                            </button>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const OtherPaymentList: React.FC = () => {
    const { 
        activeClients, 
        updateActiveClients, 
        deleteClient, 
        updateTransactions, 
    } = useClientContext();

    const { isAdmin } = useRole();
    const navigate = useNavigate();
    const tenantCode = useTenantCode();

    // Hook para eliminar cliente
    const deleteClientMutation = useDeleteClientSubscription();

    // Hook para obtener clientes con suscripciones activas
    const { data: allClients = [], isLoading: loading, isError } = useClientsAndSubscriptionsQuery(true);

    // Hooks para mutaciones de pagos
    const createPagoMutation = useCreatePagoMutation();
    const updatePagoMutation = useUpdatePagoMutation();
    const deletePagoMutation = useDeletePagoMutation();

    // Evita que un doble click cree dos pagos del mismo mes con conexión lenta
    const guardandoPago = createPagoMutation.isPending || updatePagoMutation.isPending;

    const [filteredOtherClients, setFilteredOtherClients] = React.useState<Client[]>([])
    const [fetchedOtherClientsCount, setFetchedOtherClientsCount] = React.useState<number>(0);
    const [expandedOtherClientId, setExpandedOtherClientId] = useState<string | null>(null);

    const [selectedMonthIndex, setSelectedMonthIndex] = useState<number | null>(null);
    const [selectedYear, setSelectedYear] = useState<number | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedSuscripcionId, setSelectedSuscripcionId] = useState<string | null>(null);

    const [modalMessage, setModalMessage] = useState<string>("");
    const [selectedDate, setSelectedDate] = useState<Date>(new Date());
    const [paymentExist, setPaymentExist] = useState<boolean>(false);
    const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null);

    //estados para borrado de clientes
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedClient, setSelectedClient] = useState<Client | null>(null);
    const [confirmMessage, setConfirmMessage] = useState("");

    //Filtros
    const [filter, setFilter] = useState('');
    const [monthWithoutCharge, setMonthWithoutCharge] = useState(false);
    const [monthWithoutPayment, setMonthWithoutPayment] = useState(false);
    const [anyMonthWithoutPayment, setAnyMonthWithoutPayment] = useState(false);
    const [metodoPago, setMetodoPago] = useState<MetodoPagoFiltro>('');

    //Imprimir lista
    const [showPrintList, setShowPrintList] = useState(false);


    const handleCloseModal = () => {
        setSelectedDate(new Date()); // Restablecer la fecha a hoy
        setShowModal(false);
    };

    // Efecto para sincronizar datos del query con el contexto y filtrar clientes de otros pagos
    useEffect(() => {
        if (allClients.length > 0) {
            const filteredData = allClients.filter(client => 
                client.suscripciones.some(suscripcion => suscripcion.nombreSuscripcion === "pilates-otros-pagos")
            ).map(client => ({
                ...client,
                suscripciones: client.suscripciones.filter(suscripcion => suscripcion.nombreSuscripcion === "pilates-otros-pagos")
            }));
            
            updateActiveClients(filteredData);
            setFetchedOtherClientsCount(filteredData.length);
        }
    }, [allClients, updateActiveClients]);

    // La clave es `${clienteId}-${índice de suscripción}`, no el id del cliente:
    // hay una fila por suscripción y dos filas del mismo cliente no deben abrirse
    // a la vez.
    const toggleExpand = (rowKey: string) => {
        setExpandedOtherClientId(expandedOtherClientId === rowKey ? null : rowKey);
    };

    // El icono del ojo llevaba `cursor-pointer` y ningún `onClick`: parecía un
    // botón y no hacía nada. Va a la ficha, como el "Ver detalle" de Clientes.
    const verFichaCliente = (clientId: string) => {
        navigate(`/${tenantCode}/clientes/${clientId}`);
    };

    useEffect(() => {
        const filteredData = activeClients.filter(client => 
            client.suscripciones.some(suscripcion => suscripcion.nombreSuscripcion === "pilates-otros-pagos")
        );
        setFilteredOtherClients(filteredData);
        handleFilter(filter, monthWithoutCharge, monthWithoutPayment, anyMonthWithoutPayment);
    }, [activeClients]);

    const handleMonthClick = (month: Month, id: string) => {
        //antes de mostrar el modal, comprobar si el pago ya está abierto y crear una variable de estado para mostrar un mensaje correcto en el modal
        const Selectedclient = activeClients.find(client => {
            return client.suscripciones.some(suscripcion => suscripcion._id === id);
        });

        //check if the payment is already open
        const suscripcion = Selectedclient?.suscripciones.find(suscripcion => suscripcion._id === id);

        if (!suscripcion) {
            console.error('Suscripcion no encontrada');
            return;
        }

        // Buscar pago existente por MES + AÑO (importante para diferenciar Enero 2025 de Enero 2026)
        const pagoExistente = suscripcion.pagos?.find(pago => 
            pago.mes === month.index && pago.anio === month.year
        );

        if (pagoExistente) {
            setPaymentExist(true);
            setModalMessage(`Esta a punto de <strong> CERRAR EL PAGO </strong> de <strong> ${Selectedclient?.nombre} </strong> para el mes de <strong> ${month.displayName || month.name} ${month.year} </strong>`);
            setSelectedPaymentId(pagoExistente._id ?? null);
        }else{
            setPaymentExist(false);
            setModalMessage(`Esta a punto de <strong> CREAR UN CUENTA POR COBRAR </strong> para <strong> ${Selectedclient?.nombre} </strong> del mes de <strong> ${month.displayName || month.name} ${month.year} </strong>`);
            setSelectedPaymentId(null);
        }

        setSelectedMonthIndex(month.index);
        setSelectedYear(month.year);
        setShowModal(true);
        setSelectedSuscripcionId(id);

    };

    const updatePaymentsInState = (paymentClientId: string, suscripcionId: string, updatedPayment: Pago) => {
        updateActiveClients(prevClients => {
            return prevClients.map(client => {
                if (client._id === paymentClientId) {
                    return {
                        ...client,
                        suscripciones: client.suscripciones.map(suscripcion => {
                            if (suscripcion._id === suscripcionId) {
                                const existingPaymentIndex = suscripcion.pagos.findIndex(pago => pago._id === updatedPayment._id);
                                if (existingPaymentIndex !== -1) {
                                    // Actualizar el pago existente
                                    const updatedPagos = [...suscripcion.pagos];
                                    updatedPagos[existingPaymentIndex] = updatedPayment;
                                    return {
                                        ...suscripcion,
                                        pagos: updatedPagos
                                    };
                                } else {
                                    // Añadir un nuevo pago
                                    return {
                                        ...suscripcion,
                                        pagos: [...suscripcion.pagos, updatedPayment]
                                    };
                                }
                            }
                            return suscripcion;
                        })
                    };
                }
                return client;
            });
        });
    };

    const handleConfirmOpenPay = async () => {
        // Segunda barrera: el disabled del botón puede llegar tarde si el click se repite muy rápido
        if (guardandoPago) return;

        console.log('Confirm Open Pay month:', selectedMonthIndex);
        console.log('Confirm Open Pay year:', selectedYear);
        console.log('Confirm Open Pay suscripcion_id:', selectedSuscripcionId);
        
        // Validación adicional para asegurar que el mes y el año estén definidos
        if (selectedMonthIndex === null) {
            console.error('Mes no seleccionado');
            toast.error('Error: Mes no definido');
            return;
        }

        if (!selectedYear) {
            console.error('Año no seleccionado');
            toast.error('Error: Año no definido');
            return;
        }

        if (!selectedSuscripcionId) {
            console.error('Suscripcion ID no seleccionado');
            return;
        }

        // Buscar la suscripción correcta
        const paymentClient = activeClients.find(client => 
            client.suscripciones.some(suscripcion => suscripcion._id === selectedSuscripcionId)
        );

        if (!paymentClient) {
            console.error('Cliente no encontrado');
            return;
        }

        const suscripcion = paymentClient.suscripciones.find(suscripcion => suscripcion._id === selectedSuscripcionId);

        if (!suscripcion) {
            console.error('Suscripcion no encontrada');
            return;
        }

        if(!paymentExist){
            // Obtener el metodoPago e importe de la suscripción
            const { tipoPago: metodoPago, importeSuscripcion: importe, descuento } = suscripcion;

            // Dejar observaciones en blanco
            const observaciones = '';

            // Log de datos antes de enviar al backend
            console.log('📤 Enviando pago al backend:', {
                mes: selectedMonthIndex,
                anio: selectedYear,
                importe: importe - descuento,
                metodoPago
            });

            // Crear el nuevo pago
            try {
                const paymentData = {
                    suscripcion_id: selectedSuscripcionId,
                    mes: selectedMonthIndex,
                    anio: selectedYear,
                    importe: importe - descuento,
                    metodoPago: metodoPago as 'Tarjeta' | 'Efectivo',
                    observaciones,
                    estado: 'Abierto' as const,
                    fechaPago: new Date().toISOString()
                };
                
                const result = await createPagoMutation.mutateAsync(paymentData);
                
                // Actualizar el estado de activeClients
                updatePaymentsInState(paymentClient._id, selectedSuscripcionId, result);

                toast.success('Pago creado correctamente');
            } catch (error) {
                toast.error('Error creando pago: ' + (error as Error).message, { autoClose: false });
                console.error('Error creating payment:', error);
            } finally {
                handleCloseModal();
            }

        } else {
            // Actualizar el pago existente
            try {
                if (!selectedPaymentId) {
                    throw new Error('ID de pago no seleccionado');
                }
                
                // Obtener el pago actual para mantener sus valores
                const currentPago = paymentClient.suscripciones
                    .find(s => s._id === selectedSuscripcionId)
                    ?.pagos.find(p => p._id === selectedPaymentId);
                
                if (!currentPago) {
                    throw new Error('Pago no encontrado');
                }
                
                const result = await updatePagoMutation.mutateAsync({
                    ...currentPago,
                    estado: 'Cerrado' as const,
                    fechaPago: selectedDate.toISOString()
                });
                
                // Actualizar el estado de activeClients
                updatePaymentsInState(paymentClient._id, selectedSuscripcionId, result);

                toast.success('Pago actualizado correctamente');
            } catch (error) {
                toast.error('Error actualizando pago: ' + (error as Error).message, { autoClose: false });
                console.error('Error updating payment:', error);
            } finally {
                handleCloseModal();
            }
        }
    };

    const handleDelete = (client: Client) => {
        setSelectedClient(client);
        
        // Obtener todos los pagos abiertos
        const pagosAbiertos: Pago[] = [];
        client.suscripciones.forEach(suscripcion => {
            if (suscripcion.pagos) {
                pagosAbiertos.push(...suscripcion.pagos.filter(pago => pago.estado === 'Abierto'));
            }
        });
        
        if (pagosAbiertos.length > 0) {
            const mesesFormateados = formatPendingMonths(pagosAbiertos);
            
            setConfirmMessage(
                `<div class="space-y-3">
                    <p>¿Estás seguro de que quieres dar de baja a <strong>${client.nombre.toLocaleUpperCase()}</strong>?</p>
                    <div class="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                        <div class="flex items-center">
                            <svg class="w-5 h-5 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"></path>
                            </svg>
                            <strong class="text-yellow-800">¡Atención!</strong>
                        </div>
                        <p class="text-yellow-700 mt-1">
                            Este cliente tiene <strong>${pagosAbiertos.length} pago(s) pendiente(s) de cobro</strong> correspondientes a:
                        </p>
                        <p class="text-yellow-700 font-medium">
                            ${mesesFormateados}
                        </p>
                        <p class="text-yellow-700 text-sm">
                            Estos pagos serán eliminados permanentemente.
                        </p>
                    </div>
                </div>`
            );
        } else {
            setConfirmMessage(`¿Estás seguro de que quieres dar de baja a <strong> ${client.nombre.toLocaleUpperCase()}? </strong>`);
        }
        
        setIsModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!selectedClient) return;
        console.log('Deleting client:', selectedClient);
        try {
            // Verificar si es cliente de otros pagos para incluir parámetro de eliminación de pagos
            const esClienteOtrosPagos = selectedClient.suscripciones.some(suscripcion => 
                suscripcion.nombreSuscripcion === "pilates-otros-pagos"
            );
            
            // Usar el hook para eliminar el cliente
            await deleteClientMutation.mutateAsync({
                clientId: selectedClient._id,
                subscriptionId: selectedClient.suscripciones?.[0]?._id,
                deleteOpenPayments: esClienteOtrosPagos
            });
            
            // Actualizar las listas de clientes
            const updatedClients = activeClients.filter(client => client._id !== selectedClient._id);
            
            setFilteredOtherClients(updatedClients);
            setFetchedOtherClientsCount(updatedClients.length);

            // Actualizar activeClients
            updateActiveClients(prevClients => prevClients.filter(client => client._id !== selectedClient._id));

            // Actualizar filteredClients
            setFilteredOtherClients(prevFiltered => prevFiltered.filter(client => client._id !== selectedClient._id));

            // Usamos deleteClient del contexto para actualizar el estado de los clientes eliminados
            deleteClient(selectedClient._id);
            
        } catch (error) {
            console.error('Error deleting client and subscription:', (error as Error).message);
            // El toast de error lo maneja el hook
        } finally {
            setIsModalOpen(false);
            setSelectedClient(null);
        }
    };

    const getFullMonth = () => {
        const months = [
            { name: "Enero", index: 0 },
            { name: "Febrero", index: 1 },
            { name: "Marzo", index: 2 },
            { name: "Abril", index: 3 },
            { name: "Mayo", index: 4 },
            { name: "Junio", index: 5 },
            { name: "Julio", index: 6 },
            { name: "Agosto", index: 7 },
            { name: "Septiembre", index: 8 },
            { name: "Octubre", index: 9 },
            { name: "Noviembre", index: 10 },
            { name: "Diciembre", index: 11 }
        ];
        
        const currentDate = new Date();
        const currentMonthIndex = currentDate.getMonth(); // getMonth() returns 0-11
        return months[currentMonthIndex];
    };


    //Funcion que formatea los montos con separadores de miles y decimales
    const formatNumber = (num: number): string => {
        const parts = num.toFixed(2).split('.');
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
        return parts.join(',');
    };


    //funcion que calcula el total de los importes de la suscripcion 
    // menos el total de todos los descuentos de todas las suscripciones de activeClients 
    const getTotalAmount = () => {
        let total = 0;
        filteredOtherClients.forEach(client => {
            client.suscripciones.forEach(suscripcion => {
                total += suscripcion.importeSuscripcion - (suscripcion.descuento || 0);
            });
        });
        return formatNumber(total);
    };


    // Función para manejar la eliminación de pagos
    const handleDeletePayment = (paymentId: string) => {
        console.log(`Eliminar pago con ID: ${paymentId}`);

        setSelectedPaymentId(paymentId);
        setConfirmMessage(`¿Estás seguro de que quieres <strong>ELIMINAR EL PAGO? </strong>?`);
        setIsModalOpen(true);
    };

    const confirmDeletePayment = async () => {
        // Verificar que se haya seleccionado un pago y llamar al endpoint DELETE
        if (!selectedPaymentId) {
            console.error('ID de pago no seleccionado');
            return;
        }

        try {
            await deletePagoMutation.mutateAsync(selectedPaymentId);
    
            // Actualizar el estado para reflejar la eliminación del pago
            updateActiveClients(prevClients => 
                prevClients.map(client => ({
                    ...client,
                    suscripciones: client.suscripciones.map(suscripcion => ({
                        ...suscripcion,
                        pagos: suscripcion.pagos.filter(pago => pago._id !== selectedPaymentId)
                    }))
                }))
            );
    
            setIsModalOpen(false);
            setSelectedPaymentId(null);
            toast.success('Pago eliminado exitosamente');
        } catch (error) {
            console.error('Error eliminando el pago:', error);
            toast.error('Error eliminando el pago');
        }

    };

    // Agrega una nueva función para manejar la confirmación:
    const handleConfirmAction = async () => {
        try {
            if (selectedClient) {
                await confirmDelete();
            } else if (selectedPaymentId) {
                await confirmDeletePayment();
            }
            setIsModalOpen(false);
        } catch (error) {
            console.error('Error executing action:', error);
            toast.error('Error al ejecutar la acción');
        }
    };


    //Funcion para filtrar los clientes
    const handleFilter = (filter: string, monthWithoutCharge: boolean, monthWithoutPayment: boolean, anyMonthWithoutPayment: boolean, metodo: MetodoPagoFiltro = metodoPago) => {
        const currentMonth = getFullMonth().index + 1;
        const currentYear = new Date().getFullYear();
        const lowercasedFilter = filter.toLowerCase();

        // console.log('Valores iniciales:', {
        //     currentMonth,
        //     currentYear,
        //     monthWithoutCharge,
        //     activeClientsCount: activeClients.length
        // });



        const filteredData = activeClients
        .filter(client => 
            client.suscripciones.some(suscripcion => suscripcion.nombreSuscripcion === "pilates-otros-pagos")
        )
        .filter(client => {

            const matchesFilter = client.nombre.toLowerCase().includes(lowercasedFilter) ||
                (client.email ?? '').toLowerCase().includes(lowercasedFilter) ||
                client.telefono.includes(lowercasedFilter);

            const matchesMonthWithoutCharge = !monthWithoutCharge || (
                client.suscripciones
                    .filter(sus => sus.nombreSuscripcion === "pilates-otros-pagos")
                    .every(suscripcion => 
                        !suscripcion.pagos.some(pago => 
                            pago.mes === currentMonth && 
                            pago.anio === currentYear
                        )
                    )
            );

            const matchesAnyMonthWithoutPayment = !anyMonthWithoutPayment || (
                client.suscripciones
                    .filter(sus => sus.nombreSuscripcion === "pilates-otros-pagos")
                    .some(suscripcion => 
                        suscripcion.pagos.some(pago => 
                            pago.estado === "Abierto"
                        )
                    )
            );

            const matchesOpenPayment = !monthWithoutPayment || (
                client.suscripciones
                    .filter(sus => sus.nombreSuscripcion === "pilates-otros-pagos")
                    .some(suscripcion => 
                        suscripcion.pagos.some(pago => 
                            pago.mes === currentMonth && 
                            pago.anio === currentYear && 
                            pago.estado === "Abierto"
                        )
                    )
            );
        
            const matchesMetodoPago = !metodo || client.suscripciones
                .filter(sus => sus.nombreSuscripcion === "pilates-otros-pagos")
                .some(suscripcion => suscripcion.tipoPago === metodo);

            return matchesFilter && matchesMonthWithoutCharge && matchesOpenPayment && matchesAnyMonthWithoutPayment && matchesMetodoPago;
        });
        
        // console.log('Resultados del filtro:', {
        //     filteredCount: filteredData.length,
        //     results: filteredData.map(c => c.nombre)
        // });

        setFilteredOtherClients(filteredData);
        setFetchedOtherClientsCount(filteredData.length);
        // console.log('Filter Data --> :', filteredData);
    };

    // Un único punto de entrada desde `OtherPaymentsFilter`. La exclusividad
    // entre los tres filtros la resuelve ya el componente, así que aquí solo se
    // guarda el estado y se vuelve a filtrar.
    const handleFiltersChange = (filtros: OtherPaymentsFilterState) => {
        setFilter(filtros.searchTerm);
        setMonthWithoutCharge(filtros.monthWithoutCharge);
        setMonthWithoutPayment(filtros.monthWithoutPayment);
        setAnyMonthWithoutPayment(filtros.anyMonthWithoutPayment);
        setMetodoPago(filtros.metodoPago);

        handleFilter(
            filtros.searchTerm,
            filtros.monthWithoutCharge,
            filtros.monthWithoutPayment,
            filtros.anyMonthWithoutPayment,
            filtros.metodoPago,
        );
    };

    if (loading) {
        return <p>Cargando…</p>;
    }

    if (isError) {
        return <p>Error al cargar clientes</p>;
    }

    return (
        <div className="text-sm bg-white rounded-lg shadow">
            {/* Panel de totales. Antes era un <h3> suelto encima de la lista, con el
                mes en curso en azul grande a su lado; el mismo dato en el mismo
                formato que Clientes y SEPA. */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 border-b bg-blue-50">
                <div className="flex items-center justify-center md:justify-start p-3 rounded-lg bg-white shadow-sm">
                    <Users className="h-8 w-8 text-blue-500 mr-3" />
                    <div>
                        <div className="text-xs text-gray-500">Total de registros</div>
                        <div className="text-xl font-bold">{fetchedOtherClientsCount}</div>
                    </div>
                </div>
                <div className="flex items-center justify-center md:justify-start p-3 rounded-lg bg-white shadow-sm">
                    <Euro className="h-8 w-8 text-green-500 mr-3" />
                    <div>
                        <div className="text-xs text-gray-500">Importe total</div>
                        <div className="text-xl font-bold">{getTotalAmount()}€</div>
                    </div>
                </div>
                <div className="flex items-center justify-center md:justify-start p-3 rounded-lg bg-white shadow-sm">
                    <Calendar className="h-8 w-8 text-blue-500 mr-3" />
                    <div>
                        <div className="text-xs text-gray-500">Mes en curso</div>
                        <div className="text-xl font-bold">{getFullMonth().name}</div>
                    </div>
                </div>
            </div>

            <div className="p-4 border-b flex justify-end">
                <button
                    onClick={() => setShowPrintList(true)}
                    className="bg-brand-primary hover:brightness-90 text-sm text-brand-primary-foreground px-4 py-2 rounded-lg flex items-center"
                >
                    <Printer size={16} className="mr-2" /> Imprimir listado
                </button>
            </div>

            <div id="filtros" className="p-4 border-b">
                <OtherPaymentsFilter onFilterChange={handleFiltersChange} />
            </div>
            {showPrintList && (
                <OtherPaymentsPrintList onClose={() => setShowPrintList(false)} />
            )}
            <ConfirmationModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onConfirm={handleConfirmAction}
                confirmMessage={confirmMessage}
            />
            {/* Rejilla de 12 columnas con cabecera, como la lista de SEPA. Antes
                cada cliente era una tarjeta suelta sin cabecera, así que ninguna
                columna cuadraba con la de la fila de arriba.

                Una fila POR SUSCRIPCIÓN, no por cliente: la rejilla de meses es de
                la suscripción, y un cliente puede tener más de una. */}
            <div className="overflow-x-auto">
                {filteredOtherClients.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">No se encontraron clientes.</div>
                ) : (
                    <div className="min-w-full pb-28">
                        {/* Cabecera — solo en escritorio */}
                        <div className="hidden lg:grid lg:grid-cols-12 gap-4 p-4 bg-gray-50 border-b font-medium text-sm text-gray-700">
                            <div className="col-span-6">Cliente</div>
                            <div className="col-span-2">Importe</div>
                            <div className="col-span-2">Estado</div>
                            <div className="col-span-2 text-right">Acciones</div>
                        </div>

                        {filteredOtherClients.map((client) => {
                            const suscripciones = (client.suscripciones || []).filter(
                                (suscripcion) => suscripcion.nombreSuscripcion === 'pilates-otros-pagos'
                            );

                            if (suscripciones.length === 0) {
                                return (
                                    <div key={client._id} className="border-b p-4">
                                        <div className="font-medium">{client.nombre}</div>
                                        <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">
                                            No hay suscripciones.
                                        </div>
                                    </div>
                                );
                            }

                            return suscripciones.map((suscripcion, index) => {
                                const rowKey = `${client._id}-${index}`;
                                const expandida = expandedOtherClientId === rowKey;
                                const importe = suscripcion.importeSuscripcion - (suscripcion.descuento || 0);
                                // Cuenta los pagos ABIERTOS de cualquier mes, que es el mismo
                                // criterio que el filtro "Con deudas de cualquier mes". Ojo: con
                                // el filtro "del mes en curso" el número puede ser mayor que lo
                                // que seleccionó el filtro (ahí basta uno del mes actual), por eso
                                // el tooltip lista los meses: el número solo no lo diría.
                                const pendientes = (suscripcion.pagos || []).filter((pago) => pago.estado === 'Abierto');

                                // La fila entera abre y cierra el detalle. Los botones de
                                // acción paran la propagación: si no, borrar un cliente
                                // dejaría además la fila abierta por debajo del modal.
                                const filaProps = {
                                    onClick: () => toggleExpand(rowKey),
                                    role: 'button' as const,
                                    tabIndex: 0,
                                    'aria-expanded': expandida,
                                    onKeyDown: (e: React.KeyboardEvent) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            toggleExpand(rowKey);
                                        }
                                    },
                                };

                                const estadoChip = pendientes.length === 0 ? (
                                    <span className="inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold bg-green-100 text-green-800">
                                        Al día
                                    </span>
                                ) : (
                                    <span
                                        className="inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold bg-red-100 text-red-800"
                                        title={`Pendiente: ${formatPendingMonths(pendientes)}`}
                                    >
                                        {pendientes.length} pendiente{pendientes.length > 1 ? 's' : ''}
                                    </span>
                                );

                                const acciones = (
                                    <div className="flex items-center justify-end gap-1">
                                        <IconButton
                                            tone="success"
                                            label="Ver ficha del cliente"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                verFichaCliente(client._id);
                                            }}
                                        >
                                            <Eye size={18} />
                                        </IconButton>
                                        {isAdmin ? (
                                            <IconButton
                                                tone="danger"
                                                label="Eliminar cliente y suscripción"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDelete(client);
                                                }}
                                            >
                                                <Trash2 size={18} />
                                            </IconButton>
                                        ) : (
                                            <IconButtonSlot />
                                        )}
                                    </div>
                                );

                                return (
                                    <div key={rowKey} className="border-b">
                                        {/* Fila — escritorio */}
                                        <div
                                            {...filaProps}
                                            className="hidden lg:grid lg:grid-cols-12 gap-4 p-4 hover:bg-gray-50 cursor-pointer"
                                        >
                                            {/* min-w-0 en los dos niveles, como en SEPA: sin él un hijo
                                                de flex no baja de su ancho intrínseco y el correo largo
                                                se sale por encima de la siguiente columna. */}
                                            {/* Nombre y contacto en la misma columna, apilados, como la
                                                columna Cliente de SEPA. */}
                                            <div className="col-span-6 min-w-0">
                                                <div className="flex items-center min-w-0">
                                                    <span className="text-blue-500 p-1 mr-2 shrink-0">
                                                        {expandida ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                                    </span>
                                                    <div className="min-w-0">
                                                        <div className="font-medium truncate" title={client.nombre}>{client.nombre}</div>
                                                        {/* select-all: al truncar se pierde poder arrastrar
                                                            para seleccionar, así que un clic selecciona el
                                                            valor entero y se puede copiar. */}
                                                        {client.email && (
                                                            <div className="text-sm text-gray-500 truncate select-all" title={client.email}>{client.email}</div>
                                                        )}
                                                        {client.telefono && (
                                                            <div className="text-sm text-gray-500 truncate select-all">{client.telefono}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="col-span-2">
                                                <div className="font-medium">{formatNumber(importe)}€</div>
                                                {(suscripcion.descuento ?? 0) > 0 && (
                                                    <div className="text-sm text-green-600">Desc: {formatNumber(suscripcion.descuento)}€</div>
                                                )}
                                            </div>

                                            <div className="col-span-2">{estadoChip}</div>

                                            <div className="col-span-2">{acciones}</div>
                                        </div>

                                        {/* Fila — móvil y tablet. La rejilla de 12 columnas no cabe por
                                            debajo de lg, así que ahí se apila en tarjeta. */}
                                        <div {...filaProps} className="lg:hidden p-4 cursor-pointer">
                                            <div className="flex items-start gap-2">
                                                <span className="text-blue-500 p-1 shrink-0">
                                                    {expandida ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                                </span>
                                                <div className="min-w-0 flex-1">
                                                    <div className="font-medium truncate">{client.nombre}</div>
                                                    <div className="text-sm text-gray-500 truncate">{client.email}</div>
                                                    <div className="text-sm text-gray-500">{client.telefono}</div>
                                                    <div className="mt-1 flex items-center gap-2">
                                                        <span className="font-medium">{formatNumber(importe)}€</span>
                                                        {estadoChip}
                                                    </div>
                                                </div>
                                                <div className="shrink-0">{acciones}</div>
                                            </div>
                                        </div>

                                        {/* Detalle */}
                                        {expandida && (
                                            <div className="bg-gray-50 p-4 border-t">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div className="bg-white border border-gray-200 rounded-lg p-3">
                                                        <h3 className="font-semibold text-gray-800 pb-2 text-sm border-b border-gray-200 mb-2">DATOS CLIENTE</h3>
                                                        <p className="p-1 text-xs text-gray-700"><strong>DNI:</strong> {client.dni}</p>
                                                        <p className="p-1 text-xs text-gray-700"><strong>Fecha de Alta:</strong> {new Date(client.fechaAlta).toLocaleDateString()}</p>
                                                        <p className="p-1 text-xs text-gray-700"><strong>Creado por:</strong> {client.createdBy}</p>
                                                        <p className="p-1 text-xs text-gray-700"><strong>Referencias:</strong> {client.observaciones}</p>
                                                    </div>

                                                    <div className="bg-white border border-gray-200 rounded-lg p-3">
                                                        <div className="font-semibold text-gray-800 pb-2 text-sm border-b border-gray-200 mb-2">DATOS SUSCRIPCIÓN</div>
                                                        {suscripcion.tipoClase && <p className="p-1 text-xs text-gray-700"><strong>Nivel de la Clase:</strong> {suscripcion.tipoClase}</p>}
                                                        <p className="p-1 text-xs text-gray-700"><strong>Frecuencia:</strong> {suscripcion.frecuencia}</p>
                                                        <p className="p-1 text-xs text-gray-700"><strong>Importe:</strong> {suscripcion.importeSuscripcion} €</p>
                                                        <p className="p-1 text-xs text-gray-700"><strong>Descuento:</strong> {suscripcion.descuento} €</p>
                                                        <p className="p-1 text-xs text-gray-700"><strong>Método de Pago:</strong> {suscripcion.tipoPago}</p>
                                                        <p className="p-1 text-xs text-gray-700"><strong>Fecha Alta Contrato:</strong> {new Date(suscripcion.fechaAltaContrato).toLocaleDateString()}</p>
                                                        {!suscripcion.tipoClase && (
                                                            <>
                                                                <h2 className="mb-2 text-xs mt-2"><strong className="text-gray-800">Plazas</strong></h2>
                                                                <PlazasCliente clienteId={client._id} />
                                                            </>
                                                        )}
                                                        <p className="p-1 text-xs text-gray-700"><strong>Observaciones:</strong> {suscripcion.observaciones && suscripcion.observaciones}</p>
                                                    </div>
                                                </div>

                                                {/* La rejilla de meses, que es con lo que se cobra. */}
                                                <div className="mt-4 bg-white border border-gray-200 rounded-lg p-3">
                                                    <div className="font-semibold text-gray-800 pb-2 text-sm border-b border-gray-200 mb-3 flex flex-wrap items-center gap-x-3 gap-y-1">
                                                        <span>ÚLTIMOS 3 MESES Y PRÓXIMOS 12</span>
                                                        <span className="font-normal text-xs text-gray-500">
                                                            Verde = pagado · Rojo = pendiente · Gris = sin registrar
                                                        </span>
                                                    </div>
                                                    <MesesSuscripcion
                                                        suscripcion={suscripcion}
                                                        onMonthClick={handleMonthClick}
                                                        onDeletePayment={handleDeletePayment}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            });
                        })}
                    </div>
                )}
            </div>
            
            {/* Modal para crear/cerrar pagos - fuera del map para evitar duplicados */}
            {showModal && (
                <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
                    <div className="bg-white p-6 rounded shadow-lg max-w-md">
                        <p>{parse(modalMessage)}</p>
                        {paymentExist && (
                            <div className="mt-4">
                                <label className="block mb-2">Seleccione la fecha de cobro:</label>
                                <DatePicker
                                    selected={selectedDate}
                                    onChange={(date: Date | null) => {
                                        if (date) {
                                            setSelectedDate(date);
                                        }
                                    }}
                                    dateFormat="dd/MM/yyyy"
                                    className="border p-2 rounded w-full"
                                    placeholderText="Seleccione una fecha"
                                />
                            </div>
                        )}
                        <div className='flex gap-2 p-2 mt-4'>
                            <Button onClick={handleConfirmOpenPay} loading={guardandoPago}>
                                {guardandoPago ? 'Guardando...' : 'Confirmar'}
                            </Button>
                            <Button variant="neutral" onClick={handleCloseModal} disabled={guardandoPago}>
                                Cancelar
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OtherPaymentList;